import { Component, Input, OnInit } from '@angular/core';
import { StaticFileHash } from '@scalefast/ecommerce-core';
import { ContentfulFeedbackContent } from '../../../../core/interfaces/Contentful/Feedback/ContentfulFeedbackContent';
import { SanitizeContentfulHTMLPipe } from '../../../pipes/sanitize-contentful-html.pipe';
import { AppContentfulAppsPromoComponent } from '../contentful-apps-promo/contentful-apps-promo.component';
import { AppContentfulFeedbackComponent } from '../contentful-feedback/contentful-feedback.component';
import { ContentfulAppsPromoContent } from 'src/app/core/interfaces/Contentful/AppsPromo/ContentfulAppsPromoContent';
import { ContentfulSectionContent } from 'src/app/core/interfaces/Contentful/ContentfulSectionContent';

@Component({
  selector: 'app-contentful-faq',
  templateUrl: './contentful-faq.component.html',
  styleUrls: ['./contentful-faq.component.scss'],
  standalone: true,
  imports: [AppContentfulFeedbackComponent, AppContentfulAppsPromoComponent, SanitizeContentfulHTMLPipe, StaticFileHash],
})
export class AppContentfulFAQComponent implements OnInit {
  @Input() contentfulSectionContent: ContentfulSectionContent | undefined;
  faqItems: {
    question: string;
    answer: string;
  }[] = [];
  faqFeedback: ContentfulFeedbackContent | undefined;
  faqTitle: string | undefined;
  faqDescription: string | undefined;
  appsPromo: ContentfulAppsPromoContent | undefined;

  ngOnInit() {
    if (this.contentfulSectionContent) {
      const contentfulSectionContent = this.contentfulSectionContent.contentfulContent?.content as any;

      if (contentfulSectionContent && contentfulSectionContent.answer) {
        this.faqTitle = this.formatFaqTitle(contentfulSectionContent.name);
        this.faqDescription = this.getFaqInformation(contentfulSectionContent.answer);
        this.faqFeedback = this.createFeedbackObject(contentfulSectionContent.faqFeedback);

        if (
          contentfulSectionContent.faqAppDetailsBody &&
          contentfulSectionContent.faqAppDetailsImageBackgroundDesktop &&
          contentfulSectionContent.faqAppDetailsImageBackgroundMobile &&
          contentfulSectionContent.faqAppDetailsLinks.length > 0
        ) {
          this.appsPromo = {
            bannerText: contentfulSectionContent.faqAppDetailsBody,
            bannerDesktopImgUrl: contentfulSectionContent.faqAppDetailsImageBackgroundDesktop,
            banneMobileImgUrl: contentfulSectionContent.faqAppDetailsImageBackgroundMobile,
            appsLinks: contentfulSectionContent.faqAppDetailsLinks,
          };
        }
      }
    }
  }

  formatFaqTitle(str: string) {
    // Remove "FAQ - " from the beginning of the string
    str = str.replace(/^FAQ - /, '');

    // Remove " | Preguntas frecuentes" from the end of the string
    str = str.replace(/ \| Preguntas frecuentes$/, '');

    // Remove " | Oral-B" from the end of the string
    str = str.replace(/ \| Oral-B$/, '');

    return this.formatAsciiCharacters(str);
  }

  formatAsciiCharacters(str: string) {
    let formatedText = str;

    // Change Oral-b hyphen to non breaking hyphen
    formatedText = formatedText.replace('Oral-B', 'Oral\u2011B');
    // Change non breaking spaces for regular spaces
    formatedText = formatedText.replace(/\u202F/g, ' ');

    return formatedText;
  }

  getFaqInformation(description: string) {
    // Remove duplicated line separator to avoid empty paragraphs creation
    let desc = description.replaceAll('\n\n', '\n');

    const lines = desc.split('\n');
    let formattedDesc = '';

    for (const line of lines) {
      if (line.startsWith('##')) {
        formattedDesc += `<h2>${line.substring(3).trim()}</h2>`;
      } else if (line.startsWith('-')) {
        const listItem = line.substring(1).trim();
        formattedDesc += `<ul><li><p>${listItem}</p></li></ul>`;
      } else {
        formattedDesc += `<p>${line}</p>`;
      }
    }

    return this.formatAnswer(formattedDesc.trim());
  }

  formatAnswer(answer: string): string {
    answer = this.getLinkFromString(answer).replace(/\n/g, '<br>');
    answer = answer.replace(/__(.*?)__/g, '<strong>$1</strong>');
    answer = this.formatAsciiCharacters(answer);
    return answer;
  }

  getLinkFromString(text: string): string {
    /**
     * Replaces URLs in a string with clickable links.
     *
     * Possible URL structures:
     * http://example.com/
     * (http://example.com/)
     * [example](http://example.com)
     */

    // Regular expression to match URLs with and without parentheses
    const urlRegex: RegExp = /\(?\bhttps?:\/\/[^\s<)]+\/?(?!\.)\)?/g;

    let formattedText = text.replace(urlRegex, (matchedUrl) => {
      if (matchedUrl.startsWith('(') && matchedUrl.endsWith(')')) {
        return matchedUrl;
      }

      const url = matchedUrl.endsWith('.') ? matchedUrl.slice(0, -1) : matchedUrl;
      const anchorTag = `<a href="${url}" target="_blank" class="faq-link">${url}</a>`;

      return matchedUrl.endsWith('.') ? `${anchorTag}.` : anchorTag;
    });

    // Regular expression to match URLs with markdown format
    const markdownLinkRegex: RegExp = /\[([^\]]+)\]\(([^)]+)\)/g;
    formattedText = formattedText.replace(markdownLinkRegex, '<a href="$2" target="_blank" class="faq-link">$1</a>');

    return formattedText;
  }

  createFeedbackObject(feedbackData: any): ContentfulFeedbackContent {
    return {
      feedbackName: feedbackData.name,
      feedbackTitle: feedbackData.title,
      feedbackImageId: feedbackData.desktopImage.sys.id,
      feedbackYesLabel: feedbackData.feedbackYesLabel,
      feedbackNoLabel: feedbackData.feedbackNoLabel,
      feedbackMessage: feedbackData.feedbackMessage,
    };
  }
}
